/**
 * qualityReportCore.js — ЕДИНЫЙ ОТЧЁТ О КАЧЕСТВЕ КТ И МРТ. [12.0.23]
 *
 * ПОВОД. Владелец: «заключения по КТ-качеству и МРТ-качеству разнородны, не
 * похожи друг на друга, одно вылазит на правый сайдбар, второе посредине
 * экрана». Так и было: КТ рисовалась плавающей тёмной панелью поверх правого
 * сайдбара (loaderManager), МРТ — белым модальным окном по центру
 * (rtProtocolGateModule). Разная тема, разное строение, печать только у МРТ.
 *
 * РЕШЕНИЕ. Отвечают отчёты на РАЗНЫЕ вопросы, и сливать содержание нельзя:
 * КТ оценивает исследование целиком («что по нему можно оценить»), МРТ-шлюз —
 * одну серию («годится ли для точного планирования»). Общей делается ФОРМА:
 *
 *     вердикт → паспорт серии → можно оценить → ограничения →
 *     обоснование → рекомендации → действия
 *
 * Два переходника приводят к ней каждый свой анализ, дальше всё общее:
 * разметка вкладки на токенах темы и белый лист для печати.
 *
 * Модуль чистый — без DOM и без Cornerstone. Под тестами.
 *
 * EXPORTS: отчётИзКТ, отчётИзМРТ, разметкаОтчёта, разметкаДляПечати,
 *          уровеньПоЗначку, безЗначка, ПОДПИСИ_УРОВНЕЙ
 */

/**
 * @typedef {'норма'|'осторожно'|'непригодно'|'нет'} Уровень
 *
 * @typedef {object} Пункт
 * @property {Уровень} уровень
 * @property {string} заголовок
 * @property {string} [пояснение]
 *
 * @typedef {object} ОтчётКачества
 * @property {'КТ'|'МРТ'} вид
 * @property {string} заголовок
 * @property {{уровень: Уровень, текст: string, подпись?: string}} вердикт
 * @property {Array<[string, string]>} паспорт
 * @property {Пункт[]} можно
 * @property {Пункт[]} нельзя
 * @property {string[]} обоснование
 * @property {string[]} рекомендации
 */

/** Подписи уровней — одни и те же для экрана и печати. */
export const ПОДПИСИ_УРОВНЕЙ = Object.freeze({
  норма: 'годно',
  осторожно: 'с оговорками',
  непригодно: 'не годно',
  нет: 'не определено',
});

/**
 * Уровень пункта по значку, которым его пометил КТ-анализатор.
 * Анализатор писал значки в данные («✅», «⚠️», «❌»); здесь они переводятся
 * в смысл, а на экран выводятся уже цветом и словом.
 */
export function уровеньПоЗначку(значок) {
  const з = String(значок || '');
  if (/✅|✓|✔/.test(з)) return 'норма';
  if (/⚠/.test(з)) return 'осторожно';
  if (/❌|⛔|✕|✗/.test(з)) return 'непригодно';
  return 'нет';
}

/**
 * Снять ведущий значок со строки: «🔄 Итеративная…» → «Итеративная…».
 * Эмодзи не красятся темой и по-разному выглядят на Windows и телефоне —
 * в отчёте смысл несёт текст.
 */
export function безЗначка(строка) {
  return String(строка ?? '').replace(/^[^\p{L}\p{N}«"(]+/u, '').trim();
}

const _число = (v, ед = '', знаков = 0) =>
  (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)))
    ? null : `${Number(v).toFixed(знаков)}${ед ? ' ' + ед : ''}`;

/** Паспорт: пропускаем пустые строки, чтобы таблица не пестрела прочерками. */
function _паспорт(строки) {
  return строки.filter(([, з]) => з !== null && з !== undefined && String(з).trim() !== '');
}

// ─────────────────────────────────────────────────────────────────────────
// Переходник КТ
// ─────────────────────────────────────────────────────────────────────────

/**
 * @param {object} а — результат StudyQualityAnalyzer.analyze
 * @returns {ОтчётКачества}
 */
export function отчётИзКТ(а) {
  const п = а?.params || {};
  if (!а || а.isCT === false) {
    return {
      вид: 'КТ',
      заголовок: 'Качество КТ',
      вердикт: { уровень: 'нет', текст: безЗначка(а?.summary) || 'Анализ качества доступен только для КТ' },
      паспорт: _паспорт([['Модальность', п.modality || '—']]),
      можно: [], нельзя: [], обоснование: [], рекомендации: [],
    };
  }

  const можно = (а.capabilities || []).map((к) => ({
    уровень: уровеньПоЗначку(к.icon), заголовок: безЗначка(к.feature), пояснение: безЗначка(к.detail),
  }));
  const нельзя = (а.limitations || []).map((л) => ({
    уровень: уровеньПоЗначку(л.icon) === 'норма' ? 'осторожно' : уровеньПоЗначку(л.icon),
    заголовок: безЗначка(л.feature), пояснение: безЗначка(л.detail),
  }));

  // Единого вердикта КТ-анализатор не выносит: он перечисляет задачи. Сводим
  // честно — по тому, что он сам нашёл, без новых правил поверх.
  const жёстких = нельзя.filter((н) => н.уровень === 'непригодно').length;
  const годных = можно.filter((м) => м.уровень === 'норма').length;
  const уровень = можно.length === 0 ? 'непригодно' : (жёстких || а.noiseLevel === 'high') ? 'осторожно' : 'норма';
  const шум = { low: 'низкий', moderate: 'умеренный', high: 'высокий' }[а.noiseLevel] || null;

  const матрица = (п.rows && п.columns) ? `${п.rows} × ${п.columns}` : null;
  const пиксель = (п.pixelSpacingX && п.pixelSpacingY)
    ? `${Number(п.pixelSpacingX).toFixed(3)} × ${Number(п.pixelSpacingY).toFixed(3)} мм` : null;

  return {
    вид: 'КТ',
    заголовок: 'Качество КТ: что можно оценить',
    вердикт: {
      уровень,
      текст: уровень === 'норма' ? 'Исследование годно для перечисленных задач'
        : уровень === 'осторожно' ? 'Годно с оговорками — смотрите ограничения'
          : 'Задач, для которых исследование годно, не найдено',
      подпись: `годно для ${годных} из ${можно.length} · ограничений ${нельзя.length}`,
    },
    паспорт: _паспорт([
      ['Пациент', [п.patientName, п.patientId ? `ID ${п.patientId}` : ''].filter(Boolean).join(' · ') || null],
      ['Аппарат', [а.scanner?.brand, а.scanner?.model || п.modelName].filter(Boolean).join(' ') || null],
      ['Учреждение', п.institution || null],
      ['Серия', а.серия?.описание || п.seriesDesc || null],   // [12.0.24]
      ['Протокол', п.protocolName || null],
      ['Контраст', безЗначка(а.phase?.label) || null],
      // [12.0.24] Откуда известно про контраст: теги, описание серии или
      // вывод из отсутствия признаков. Врачу эта разница важна.
      ['Как определён контраст', а.params?.contrastSource?.основание || null],
      ['Напряжение', _число(п.kvp, 'кВ')],
      ['Ток / экспозиция', [_число(п.mA, 'мА'), _число(п.mAs, 'мАс')].filter(Boolean).join(' · ') || null],
      ['Толщина среза', _число(п.sliceThickness, 'мм', 2)],
      ['Ядро реконструкции', п.kernel || null],
      ['Реконструкция', а.recon && а.recon.type !== 'unknown' ? безЗначка(а.recon.label) : null],
      ['Поле обзора', _число(п.fov, 'мм', 1)],
      ['Пиксель', пиксель],
      ['Матрица', матрица],
      ['Шум', шум],
    ]),
    можно,
    нельзя,
    обоснование: [],
    рекомендации: (а.recommendations || []).map(безЗначка).filter(Boolean),
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Переходник МРТ
// ─────────────────────────────────────────────────────────────────────────

/**
 * @param {object} р — результат шлюза (checkSeries → res)
 * @param {object} т — теги серии (checkSeries → tags)
 * @returns {ОтчётКачества}
 */
export function отчётИзМРТ(р, т = {}) {
  const уровень = р?.level === 'fail' ? 'непригодно' : р?.level === 'warn' ? 'осторожно' : р ? 'норма' : 'нет';
  const текст = {
    непригодно: 'Не субмиллиметр — не для точного планирования',
    осторожно: 'Почти субмиллиметр — есть замечания (для 3D обычно приемлемо)',
    норма: 'Субмиллиметр подтверждён',
    нет: 'Серия не проверена',
  }[уровень];
  const рекон = (т.pixelSpacing && т.pixelSpacing[0]) ? `${Number(т.pixelSpacing[0]).toFixed(2)} мм` : null;
  const м = (x) => (Array.isArray(x) && x.length >= 2) ? `${x[0]} × ${x[1]}` : null;

  return {
    вид: 'МРТ',
    заголовок: 'Качество МРТ: субмиллиметровый шлюз',
    вердикт: {
      уровень,
      текст,
      подпись: 'Годится ли серия для точного планирования: реальный размер вокселя против заявленного',
    },
    паспорт: _паспорт([
      ['Пациент', [т.patientName, т.patientId ? `ID ${т.patientId}` : ''].filter(Boolean).join(' · ') || null],
      ['Серия', [т.seriesDescription || т.protocolName, р?.modality].filter(Boolean).join(' · ') || null],
      ['Тип съёмки', р?.mrType || null],
      ['Толщина среза', _число(р?.throughMm, 'мм', 2)],
      ['Реальный воксель в плоскости', _число(р?.trueInPlaneMm, 'мм', 2)],
      ['Воксель по реконструкции', рекон ? рекон + (р?.interpolated ? ' (интерполяция)' : '') : null],
      ['Матрица съёмки', м(р?.acqMatrix)],
      ['Матрица реконструкции', м(р?.reconMatrix)],
      ['Поле обзора', м(р?.fovMm) ? м(р.fovMm) + ' мм' : null],
      ['Порог субмиллиметра', _число(р?.thresholdMm, 'мм', 1)],
    ]),
    можно: [],
    нельзя: [],
    обоснование: (р?.reasons || []).map((x) => String(x)),
    рекомендации: [],
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Разметка
// ─────────────────────────────────────────────────────────────────────────

const _э = (т) => String(т ?? '').replace(/[&<>"']/g, (с) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[с]));

function _пункты(список) {
  return список.map((п) => `
    <li class="кч-пункт кч-${п.уровень}">
      <span class="кч-метка">${_э(ПОДПИСИ_УРОВНЕЙ[п.уровень])}</span>
      <div><div class="кч-пункт-заг">${_э(п.заголовок)}</div>
      ${п.пояснение ? `<div class="кч-пункт-пояс">${_э(п.пояснение)}</div>` : ''}</div>
    </li>`).join('');
}

/**
 * Разметка отчёта для вкладки — классы кч-*, цвета из темы (index.html).
 * Кнопки действий несут data-действие; обработчики вешает qualityTab.js.
 * @param {ОтчётКачества} о
 */
export function разметкаОтчёта(о) {
  const раздел = (заг, тело) => тело ? `<section class="кч-раздел"><h4 class="кч-заг">${заг}</h4>${тело}</section>` : '';
  return `
    <div class="кч-отчёт" data-вид="${_э(о.вид)}">
      <div class="кч-вердикт кч-${о.вердикт.уровень}">
        <div class="кч-вердикт-заг">${_э(о.заголовок)}</div>
        <div class="кч-вердикт-текст">${_э(о.вердикт.текст)}</div>
        ${о.вердикт.подпись ? `<div class="кч-вердикт-подпись">${_э(о.вердикт.подпись)}</div>` : ''}
      </div>
      ${раздел('Паспорт серии', о.паспорт.length ? `<table class="кч-паспорт">${о.паспорт.map(([к, з]) =>
        `<tr><td>${_э(к)}</td><td>${_э(з)}</td></tr>`).join('')}</table>` : '')}
      ${раздел('Что можно оценить', о.можно.length ? `<ul class="кч-список">${_пункты(о.можно)}</ul>` : '')}
      ${раздел('Ограничения', о.нельзя.length ? `<ul class="кч-список">${_пункты(о.нельзя)}</ul>` : '')}
      ${раздел('Обоснование', о.обоснование.length ? `<ul class="кч-текст">${о.обоснование.map((x) => `<li>${_э(x)}</li>`).join('')}</ul>` : '')}
      ${раздел('Рекомендации', о.рекомендации.length ? `<ul class="кч-текст">${о.рекомендации.map((x) => `<li>${_э(x)}</li>`).join('')}</ul>` : '')}
      <div class="кч-действия">
        <button class="rt-btn rt-btn--sm" data-действие="снова">Проверить снова</button>
        <button class="rt-btn rt-btn--sm rt-btn--primary" data-действие="печать">Печать заключения</button>
      </div>
    </div>`;
}

/**
 * Белый лист для печати — та же схема, что на экране.
 * Белый здесь уместен: это бумага, а не окно тёмной программы.
 * @param {ОтчётКачества} о
 * @param {{автор?: string, дата?: string}} [подвал]
 */
export function разметкаДляПечати(о, подвал = {}) {
  const цвет = { норма: '#127a2e', осторожно: '#b26a00', непригодно: '#c0161c', нет: '#5a6670' }[о.вердикт.уровень];
  const пункты = (список) => список.map((п) => `<li><b>${_э(п.заголовок)}</b> — ${_э(ПОДПИСИ_УРОВНЕЙ[п.уровень])}`
    + `${п.пояснение ? `<br><span class="п">${_э(п.пояснение)}</span>` : ''}</li>`).join('');
  const раздел = (заг, тело) => тело ? `<h3>${заг}</h3>${тело}` : '';
  return `<!DOCTYPE html><html lang="ru"><head><meta charset="utf-8"><title>${_э(о.заголовок)}</title>
<style>
  body{font:13px/1.55 -apple-system,"Segoe UI",Arial,sans-serif;color:#12222b;margin:24px;}
  .в{border-left:6px solid ${цвет};padding:10px 14px;background:#f6f8f9;margin-bottom:14px}
  .в .з{font-size:17px;font-weight:700} .в .т{font-weight:600;color:${цвет}} .в .п{color:#555;font-size:12px}
  table{border-collapse:collapse;width:100%} td{padding:5px 8px;border-bottom:1px solid #e3e7ea}
  td:first-child{color:#555;width:45%} h3{font-size:14px;margin:16px 0 6px} ul{margin:0;padding-left:20px}
  li{margin:3px 0} .п{color:#555} footer{margin-top:24px;color:#777;font-size:11px}
</style></head><body>
  <div class="в"><div class="з">${_э(о.заголовок)}</div><div class="т">${_э(о.вердикт.текст)}</div>
    ${о.вердикт.подпись ? `<div class="п">${_э(о.вердикт.подпись)}</div>` : ''}</div>
  ${раздел('Паспорт серии', о.паспорт.length ? `<table>${о.паспорт.map(([к, з]) => `<tr><td>${_э(к)}</td><td>${_э(з)}</td></tr>`).join('')}</table>` : '')}
  ${раздел('Что можно оценить', о.можно.length ? `<ul>${пункты(о.можно)}</ul>` : '')}
  ${раздел('Ограничения', о.нельзя.length ? `<ul>${пункты(о.нельзя)}</ul>` : '')}
  ${раздел('Обоснование', о.обоснование.length ? `<ul>${о.обоснование.map((x) => `<li>${_э(x)}</li>`).join('')}</ul>` : '')}
  ${раздел('Рекомендации', о.рекомендации.length ? `<ul>${о.рекомендации.map((x) => `<li>${_э(x)}</li>`).join('')}</ul>` : '')}
  <footer>${_э(подвал.дата || '')}${подвал.автор ? ' · ' + _э(подвал.автор) : ''} · Не для диагностики</footer>
</body></html>`;
}
